import { useState, useEffect } from "react";
import { Button, Tooltip, Typography } from "antd";
import HistoryIcon from "../../assets/icons/history.svg?react";
const { Text } = Typography;

function formatDateLong(
  dateString: string | null | undefined,
  locale = "en-US",
) {
  const date = new Date(dateString ?? "");

  const time = date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const dateFormatted = date.toLocaleDateString(locale, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return `${time} | ${dateFormatted}`;
}

import { PlusOutlined } from "@ant-design/icons";
import GuideButton from "../UI/GuideButton";
import DeleteIcon from "../../assets/icons/delete.svg?react";
import {
  AV_DATE_TEXT,
  AV_DELETE_COLOR,
  AV_HISTORY_BUTTON_BG,
  AV_HISTORY_CONFIRM_TEXT,
  AV_PANEL_COLOR,
} from "../AskVivo/askVivo.constants";

interface ChatHeaderProps {
  prompt: string;
  isNewSession: boolean;
  handleViewSessionHistory: () => void;
  handleIsNewSession: () => void;
  oldSessionTimeStamp: string | null;
  isSidePanelOpen: boolean;
  isHistoryItem?: boolean;
  onDeleteSession: () => void;
}

export default function ChatHeader({
  prompt,
  isNewSession,
  handleViewSessionHistory,
  handleIsNewSession,
  oldSessionTimeStamp,
  isSidePanelOpen,
  isHistoryItem = false,
  onDeleteSession,
}: ChatHeaderProps) {
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
  );
  const [currentDate, setCurrentDate] = useState(
    new Date().toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }),
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(
        new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      );
      setCurrentDate(
        new Date().toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
      );
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const sessionDate = formatDateLong(oldSessionTimeStamp);

  const showCurrentDate = () => {
    return (
      <p className="text-sm mt-2" style={{ color: AV_DATE_TEXT }}>
        {currentTime} <span className="ml-1 mr-2">|</span>
        {currentDate}
      </p>
    );
  };

  const handleViewHistory = () => {
    handleViewSessionHistory();
  };
  const handleNewSession = () => {
    handleIsNewSession();
  };

  return (
    <div className="flex justify-between">
      <div className="ml-3">
        {isNewSession ? (
          showCurrentDate()
        ) : (
          <div className="text-sm mt-2" style={{ color: AV_DATE_TEXT }}>
            {oldSessionTimeStamp ? sessionDate : showCurrentDate()}
          </div>
        )}
        <Text
          style={{ maxWidth: 150, verticalAlign: "0px" }}
          ellipsis={{ tooltip: prompt }}
          className="chat-header-text text-base font-semibold mb-2"
        >
          {isNewSession ? "New Chat" : prompt}
        </Text>
      </div>

      <div className="flex items-center gap-3 mb-7 mr-3">
        <GuideButton
          collapsed={isSidePanelOpen}
          pageName="/wiki/guides/inquiry-askvivo"
          isAskVivo={true}
        />
        <Tooltip title="View your past sessions and results">
          <Button
            data-testid="view-history"
            className={`askvivo-header-action ${isSidePanelOpen ? "responsive-button" : ""}`}
            style={{
              background: AV_HISTORY_BUTTON_BG,
              color: AV_HISTORY_CONFIRM_TEXT,
              borderColor: AV_PANEL_COLOR,
            }}
            onClick={handleViewHistory}
          >
            <HistoryIcon />
            <span className="button-text hide-on-small">History</span>
          </Button>
        </Tooltip>
        {isHistoryItem && (
          <Tooltip title="Delete this session">
            <Button
              data-testid="delete-session-button"
              onClick={onDeleteSession}
              className={`askvivo-header-action ${
                isSidePanelOpen ? "responsive-button" : ""
              }`}
              style={{
                background: "#fff",
                borderColor: AV_DELETE_COLOR,
                color: AV_DELETE_COLOR,
                alignItems: "center",
              }}
            >
              <DeleteIcon
                style={{
                  width: "24px",
                  height: "24px",
                  color: AV_DELETE_COLOR,
                }}
              />
              <span className="button-text hide-on-small">Delete</span>
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Start a new chat">
          <Button
            data-testid="new-chat-button"
            className={`askvivo-header-action ${
              isSidePanelOpen ? "responsive-button" : ""
            }`}
            type="primary"
            onClick={handleNewSession}
            icon={<PlusOutlined className="text-[16px]" />}
          >
            <span className="button-text hide-on-small">New Chat</span>
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}
